import { Rnd } from 'react-rnd'
import { Minus, X, Maximize2 } from 'lucide-react'
import { CanvasWidget as ICanvasWidget } from '../../types'
import { useTaskStore } from '../../store/taskStore'
import { widgetByType } from './widgetRegistry'
import clsx from 'clsx'
import { Suspense, useEffect, useState } from 'react'

interface Props {
  widget: ICanvasWidget
}

export default function CanvasWidgetWrapper({ widget }: Props) {
  const { updateCanvasWidget, removeCanvasWidget, bringToFront } = useTaskStore()
  const meta = widgetByType[widget.type]
  if (!meta) return null

  const { Component, Icon, label, color } = meta

  const toggleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation()
    updateCanvasWidget(widget.instanceId, { minimized: !widget.minimized })
  }

  const close = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeCanvasWidget(widget.instanceId)
  }

  const handleClick = () => bringToFront(widget.instanceId)

  const HEADER_H = 36
  const safeX = Math.max(0, widget.x)
  const safeY = Math.max(0, widget.y)
  const [position, setPosition] = useState({ x: safeX, y: safeY })

  useEffect(() => {
    setPosition({ x: Math.max(0, widget.x), y: Math.max(0, widget.y) })
  }, [widget.x, widget.y])

  return (
    <Rnd
      position={position}
      size={{ width: widget.width, height: widget.minimized ? HEADER_H : widget.height }}
      minWidth={220}
      minHeight={widget.minimized ? HEADER_H : 120}
      dragHandleClassName="widget-drag-handle"
      onDrag={(_, d) => setPosition({ x: Math.max(0, d.x), y: Math.max(0, d.y) })}
      onDragStop={(_, d) => {
        const nextPosition = { x: Math.max(0, d.x), y: Math.max(0, d.y) }
        setPosition(nextPosition)
        updateCanvasWidget(widget.instanceId, nextPosition)
      }}
      onDragStart={() => bringToFront(widget.instanceId)}
      onResizeStop={(_, __, ref, ___, pos) => {
        updateCanvasWidget(widget.instanceId, {
          width: parseInt(ref.style.width),
          height: parseInt(ref.style.height),
          x: Math.max(0, pos.x),
          y: Math.max(0, pos.y),
        })
      }}
      style={{ zIndex: widget.zIndex }}
      onClick={handleClick}
      enableResizing={!widget.minimized}
      className="canvas-widget select-none"
    >
      <div
        className={clsx(
          'flex flex-col w-full h-full rounded-xl shadow-xl border border-white/20 dark:border-slate-800/80 overflow-hidden',
          'bg-white dark:bg-slate-900 dark:text-slate-100'
        )}
        style={{ height: '100%' }}
        onPointerDown={handleClick}
      >
        {/* Header */}
        <div
          className={clsx(
            'widget-drag-handle flex items-center gap-2 px-3 cursor-grab active:cursor-grabbing shrink-0 select-none',
            color
          )}
          style={{ height: HEADER_H }}
        >
          <Icon size={13} className="text-white/90 shrink-0" />
          <span className="text-white text-xs font-semibold flex-1 truncate">{label}</span>
          <div className="flex items-center gap-0.5">
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={toggleMinimize}
              className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
              title={widget.minimized ? 'Expandir' : 'Minimizar'}
              aria-label={widget.minimized ? `Expandir ${label}` : `Minimizar ${label}`}
            >
              {widget.minimized ? <Maximize2 size={11} /> : <Minus size={11} />}
            </button>
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={close}
              className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
              title="Fechar"
              aria-label={`Remover widget ${label}`}
            >
              <X size={11} />
            </button>
          </div>
        </div>

        {/* Content */}
        {!widget.minimized && (
          <div className="flex-1 overflow-auto bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
            <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-slate-500 dark:text-slate-400" role="status">Carregando widget…</div>}><Component /></Suspense>
          </div>
        )}
      </div>
    </Rnd>
  )
}
